import type { EstimateUnits } from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import cx from 'classnames'
import type React from 'react'
import { useState } from 'react'
import { useSelector } from 'react-redux'

import { useArtBoardDisabled } from '@/hooks/selectors/useArtBoardDisabled'
import { divideNumberIntoDigits } from '../../helper'
import { Input } from '../Input'
import css from './SummaryTable.module.scss'

type Props = {
  vat: number
  currency: string
  unit: EstimateUnits
  onVatChange: (value: number) => void
}

export const SummaryTable: React.FC<Props> = ({
  vat,
  currency,
  unit,
  onVatChange,
}) => {
  const [selectedCell, setSelectedCell] = useState(false)

  const { subtotal, tax, total } = useSelector(
    migrationProjectSelector.getProjectEstimateTotal
  )

  const isReadOnly = useSelector(
    migrationProjectSelector.getProjectLegacyAccessTypeIsReadOnly
  )

  const artboardDisabled = useArtBoardDisabled()
  const locked = isReadOnly || artboardDisabled

  const taxEnabled = useSelector(
    migrationProjectSelector.getProjectEstimateTaxEnabled
  )

  const handleSelectCell = () => {
    setSelectedCell(true)
  }

  const resetSelectedCell = () => {
    setSelectedCell(false)
  }

  const handleOnChange = (value: string | number) => {
    if (Number.isNaN(+value) || +value < 0) {
      onVatChange(vat)
      return
    }

    onVatChange(+value)
  }

  const renderAmount = String(subtotal.amount).length < 7
  const renderUnit = String(subtotal.hours).length >= 5 ? null : unit
  const renderVat = String(vat).length >= 5 ? null : '%'

  return (
    <div className={css.container}>
      <div className={css.table}>
        <div className={css.tableRow}>
          <div className={css.tableCell}>Subtotal</div>
          <div
            className={cx(css.tableCell, css.disabled, {
              [css.flexStart]: !renderUnit,
            })}
          >
            <>
              {divideNumberIntoDigits(subtotal.hours)}
              {renderUnit}
            </>
          </div>
          <div
            className={cx(css.tableCell, css.disabled, {
              [css.flexStart]: !renderAmount,
            })}
          >
            {divideNumberIntoDigits(subtotal.amount)}
          </div>
        </div>
        {taxEnabled && (
          <div className={css.tableRow}>
            <div className={css.tableCell}>Tax</div>
            <div
              className={cx(css.tableCell, {
                [css.selected]: selectedCell,
                [css.pointer]: !selectedCell && !locked,
                [css.disabled]: locked,
              })}
              onClick={() => (!locked ? handleSelectCell() : undefined)}
            >
              {selectedCell ? (
                <Input
                  name="tax"
                  defaultValue={vat}
                  textAlign="right"
                  onChange={handleOnChange}
                  onResetSelectedCall={resetSelectedCell}
                />
              ) : (
                <>
                  {vat}
                  {renderVat}
                </>
              )}
            </div>
            <div className={cx(css.tableCell, css.disabled)}>
              {divideNumberIntoDigits(tax.amount)}
            </div>
          </div>
        )}
      </div>

      <div className={css.totalWrap}>
        <span>Total</span>
        <span>
          {currency} {divideNumberIntoDigits(total)}
        </span>
      </div>
    </div>
  )
}
